import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { useLeaderboard } from "@/hooks/useEventLeaderboard";
import {
  Search,
  Loader2,
  TrendingUp,
  TrendingDown,
  Brain,
  ArrowLeft,
  Clock,
  ChevronDown,
} from "lucide-react";
import { ensureUsdtSymbol, removeUsdtSuffix } from "@/utils/symbols";
import {
  usePreviousAlerts,
  useLeaderboardAlerts,
  useIncomingListings,
} from "@/hooks/useCryptoAlerts";
import { useTokenMetadata } from "@/hooks/useTokenMetadata";
import {
  TradingViewChartWidget,
} from "@crypto-trading/ui-shared/client";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { LeaderboardTokenTile } from "@/components/LeaderboardTokenTile";
import { IncomingListingTile } from "@/components/IncomingListingTile";
import { TokenIcon } from "@/components/TokenIcon";
import { AlertHistory } from "@/components/AlertHistory";
import { useTokenAvailability } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import { ReferralBottomNav } from "@/components/ReferralBottomNav";
import { PositionTradeDrawer } from "@/components/PositionTradeDrawer";

const MOBILE_INITIAL_COUNT = 3;
const MOBILE_MAX_EXPANDED_COUNT = 6;

/**
 * Mobile version of the Analyze page
 * - Single column layout
 * - 3 items + "Show more" for incoming/leaderboards
 * - Back FAB when viewing token analysis
 */
export const AnalyzePageMobile: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const symbolFromUrl = searchParams.get("symbol");
  const [inputSymbol, setInputSymbol] = useState("");
  const [analyzedSymbol, setAnalyzedSymbol] = useState<string | null>(
    symbolFromUrl
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [showAllIncoming, setShowAllIncoming] = useState(false);
  const [showAllGainers, setShowAllGainers] = useState(false);
  const [showAllLosers, setShowAllLosers] = useState(false);

  useEffect(() => {
    if (symbolFromUrl !== analyzedSymbol) {
      setAnalyzedSymbol(symbolFromUrl);
    }
  }, [symbolFromUrl]);

  const handleAnalyzeSymbol = useCallback(
    (symbol: string) => {
      const normalized = ensureUsdtSymbol(symbol.trim().toUpperCase());
      setAnalyzedSymbol(normalized);
      setSearchParams({ symbol: removeUsdtSuffix(normalized) });
    },
    [setSearchParams]
  );

  const handleBackToDashboard = useCallback(() => {
    setAnalyzedSymbol(null);
    setInputSymbol("");
    setSearchParams({});
  }, [setSearchParams]);

  const normalizedSymbol = useMemo(
    () => (analyzedSymbol ? ensureUsdtSymbol(analyzedSymbol) : ""),
    [analyzedSymbol]
  );

  const baseSymbol = useMemo(
    () => (analyzedSymbol ? removeUsdtSuffix(analyzedSymbol) : ""),
    [analyzedSymbol]
  );

  const isAnalysisView = !!analyzedSymbol;

  const { data: leaderboardData, isLoading: isLoadingLeaderboard } =
    useLeaderboardAlerts(!isAnalysisView);
  const { data: incomingListings = [], isLoading: isLoadingIncoming } =
    useIncomingListings(!isAnalysisView);

  const {
    sortedGainers,
    sortedLosers,
    pricedGainers,
    pricedLosers,
    isPriceLoading,
    handlePriceUpdate,
  } = useLeaderboard(leaderboardData, isLoadingLeaderboard);

  const visibleGainersIds = useMemo(
    () =>
      new Set(
        pricedGainers
          .slice(
            0,
            showAllGainers ? MOBILE_MAX_EXPANDED_COUNT : MOBILE_INITIAL_COUNT
          )
          .map((a) => a.id)
      ),
    [pricedGainers, showAllGainers]
  );

  const visibleLosersIds = useMemo(
    () =>
      new Set(
        pricedLosers
          .slice(
            0,
            showAllLosers ? MOBILE_MAX_EXPANDED_COUNT : MOBILE_INITIAL_COUNT
          )
          .map((a) => a.id)
      ),
    [pricedLosers, showAllLosers]
  );

  const {
    data: tokenMetadata,
    isLoading: isLoadingMetadata,
    error: metadataError,
  } = useTokenMetadata(baseSymbol, isAnalysisView);

  const { data: previousAlerts = [], isLoading: isLoadingAlerts } =
    usePreviousAlerts(userId, baseSymbol, "", isAnalysisView);

  const { data: exchanges = [] } = useUserExchanges(userId, true);
  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, isAnalysisView);

  const globallyAvailableExchangeCodes = useMemo(() => {
    const allExchanges = tokenAvailability?.userExchanges ?? [];
    return allExchanges.filter(
      (code) => !code.endsWith("-demo") && !code.endsWith("-testnet")
    );
  }, [tokenAvailability?.userExchanges]);

  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );

  const supportedUserExchangeCodes = useMemo(() => {
    return exchanges
      .filter((e) => !e.code.endsWith("-demo") && !e.code.endsWith("-testnet"))
      .map((e) => e.code)
      .filter((code) => globallyAvailableSet.has(code));
  }, [exchanges, globallyAvailableSet]);

  const hasTradableExchange = supportedUserExchangeCodes.length > 0;

  const handleAnalyze = useCallback(() => {
    if (!inputSymbol.trim()) return;
    setIsAnalyzing(true);
    handleAnalyzeSymbol(inputSymbol);
    setTimeout(() => setIsAnalyzing(false), 500);
  }, [inputSymbol, handleAnalyzeSymbol]);

  const visibleIncoming = showAllIncoming
    ? incomingListings
    : incomingListings.slice(0, MOBILE_INITIAL_COUNT);

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <h1 className="text-xl font-bold text-text-primary mb-4">
            {isAnalysisView ? `Analyze ${baseSymbol}` : "Analyze Token"}
          </h1>

          {/* Search */}
          <div className="flex gap-2 mb-6">
            <div className="flex-1 relative">
              <input
                type="text"
                value={isAnalysisView ? baseSymbol : inputSymbol}
                onChange={(e) =>
                  isAnalysisView ? null : setInputSymbol(e.target.value)
                }
                onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
                placeholder="Enter token (e.g. BTC, ETH)"
                className="w-full px-4 py-3 bg-secondary border border-border rounded-xl text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent/50"
                readOnly={isAnalysisView}
              />
              <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary pointer-events-none" />
            </div>
            {!isAnalysisView && (
              <button
                onClick={handleAnalyze}
                disabled={!inputSymbol.trim() || isAnalyzing}
                className="px-4 py-3 bg-accent hover:bg-accent/80 disabled:bg-accent/50 text-white font-medium rounded-xl flex items-center gap-2"
              >
                {isAnalyzing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Brain className="w-5 h-5" />
                    <span>Analyze</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Dashboard View */}
          {!isAnalysisView && (
            <div className="space-y-6">
              {/* Top Gainers */}
              <div className="bg-secondary border border-border rounded-xl p-4">
                <h3 className="text-base font-bold text-text-primary flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  Top Gainers
                </h3>
                {isLoadingLeaderboard ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-accent" />
                  </div>
                ) : (
                  <>
                    {/* Tiles always rendered so WS subscriptions are active.
                        Hidden via CSS during price loading — never unmounted. */}
                    {isPriceLoading && (
                      <div className="flex justify-center py-6">
                        <Loader2 className="w-6 h-6 animate-spin text-accent" />
                      </div>
                    )}
                    {!isPriceLoading && visibleGainersIds.size === 0 && (
                      <p className="text-sm text-text-secondary py-2">
                        No listings with price data
                      </p>
                    )}
                    <div className="space-y-2">
                      {sortedGainers.map((alert) => (
                        <div
                          key={`gainers-${alert.id}`}
                          className={
                            isPriceLoading || !visibleGainersIds.has(alert.id)
                              ? "hidden"
                              : ""
                          }
                        >
                          <LeaderboardTokenTile
                            alert={alert}
                            onPriceUpdate={handlePriceUpdate}
                            onAnalyze={handleAnalyzeSymbol}
                          />
                        </div>
                      ))}
                    </div>
                    {!isPriceLoading &&
                      pricedGainers.length > MOBILE_INITIAL_COUNT && (
                        <button
                          onClick={() => setShowAllGainers(!showAllGainers)}
                          className="w-full mt-3 py-2 flex items-center justify-center gap-2 text-accent text-sm font-medium"
                        >
                          {showAllGainers ? "Show less" : "Show more"}
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${
                              showAllGainers ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      )}
                  </>
                )}
              </div>

              {/* Top Losers */}
              <div className="bg-secondary border border-border rounded-xl p-4">
                <h3 className="text-base font-bold text-text-primary flex items-center gap-2 mb-3">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                  Top Losers
                </h3>
                {isLoadingLeaderboard ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-accent" />
                  </div>
                ) : (
                  <>
                    {isPriceLoading && (
                      <div className="flex justify-center py-6">
                        <Loader2 className="w-6 h-6 animate-spin text-accent" />
                      </div>
                    )}
                    {!isPriceLoading && visibleLosersIds.size === 0 && (
                      <p className="text-sm text-text-secondary py-2">
                        No listings with price data
                      </p>
                    )}
                    <div className="space-y-2">
                      {sortedLosers.map((alert) => (
                        <div
                          key={`losers-${alert.id}`}
                          className={
                            isPriceLoading || !visibleLosersIds.has(alert.id)
                              ? "hidden"
                              : ""
                          }
                        >
                          <LeaderboardTokenTile
                            alert={alert}
                            onPriceUpdate={handlePriceUpdate}
                            onAnalyze={handleAnalyzeSymbol}
                          />
                        </div>
                      ))}
                    </div>
                    {!isPriceLoading &&
                      pricedLosers.length > MOBILE_INITIAL_COUNT && (
                        <button
                          onClick={() => setShowAllLosers(!showAllLosers)}
                          className="w-full mt-3 py-2 flex items-center justify-center gap-2 text-accent text-sm font-medium"
                        >
                          {showAllLosers ? "Show less" : "Show more"}
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${
                              showAllLosers ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      )}
                  </>
                )}
              </div>

              {/* Incoming Listings */}
              <div className="bg-secondary border border-border rounded-xl p-4">
                <h3 className="text-base font-bold text-text-primary flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-amber-400" />
                  Incoming Listings
                </h3>
                {isLoadingIncoming ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-accent" />
                  </div>
                ) : incomingListings.length === 0 ? (
                  <p className="text-sm text-text-secondary py-2">
                    No incoming listings
                  </p>
                ) : (
                  <>
                    <div className="space-y-2">
                      {visibleIncoming.map((alert) => (
                        <IncomingListingTile
                          key={alert.id}
                          alert={alert}
                          onAnalyze={handleAnalyzeSymbol}
                        />
                      ))}
                    </div>
                    {incomingListings.length > MOBILE_INITIAL_COUNT && (
                      <button
                        onClick={() => setShowAllIncoming(!showAllIncoming)}
                        className="w-full mt-3 py-2 flex items-center justify-center gap-2 text-accent text-sm font-medium"
                      >
                        {showAllIncoming ? "Show less" : "Show more"}
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            showAllIncoming ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Analysis View */}
          {isAnalysisView && (
            <div className="space-y-4">
              <div className="bg-secondary border border-border rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <TokenIcon
                    symbol={baseSymbol}
                    logo={tokenMetadata?.logo}
                    size="lg"
                  />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-text-primary truncate">
                      {baseSymbol}
                    </h2>
                    {tokenMetadata?.name && (
                      <p className="text-sm text-text-secondary truncate">
                        {tokenMetadata.name}
                      </p>
                    )}
                    {(isLoadingMetadata ||
                      isLoadingAlerts ||
                      isLoadingAvailability) && (
                      <Loader2 className="w-4 h-4 animate-spin text-accent mt-1" />
                    )}
                    {metadataError && !isLoadingMetadata && (
                      <p className="text-xs text-yellow-500">
                        Could not load metadata
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {tokenMetadata?.description && (
                <div className="bg-secondary border border-border rounded-xl p-4">
                  <h3 className="text-sm font-bold text-text-primary mb-2">
                    Description
                  </h3>
                  <p className="text-sm text-text-primary leading-relaxed line-clamp-4">
                    {tokenMetadata.description}
                  </p>
                </div>
              )}

              <AlertHistory alerts={previousAlerts} variant="page-mobile" />

              {globallyAvailableExchangeCodes.length > 0 && (
                <div className="bg-secondary border border-border rounded-xl p-4">
                  <h3 className="text-sm font-bold text-text-primary mb-2">
                    Exchange Availability
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {globallyAvailableExchangeCodes.slice(0, 6).map((code) => (
                      <span
                        key={code}
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          supportedUserExchangeCodes.includes(code)
                            ? "bg-green-500/20 text-green-400"
                            : "bg-gray-500/20 text-gray-400"
                        }`}
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                  {hasTradableExchange && (
                    <button
                      onClick={() => setIsTradeDrawerOpen(true)}
                      className="mt-3 w-full py-2 bg-accent hover:bg-accent/80 text-white text-sm font-medium rounded-lg"
                    >
                      Trade
                    </button>
                  )}
                </div>
              )}

              <div className="bg-secondary border border-border rounded-xl p-4">
                <h3 className="text-sm font-bold text-text-primary mb-3">
                  Price Chart
                </h3>
                <TradingViewChartWidget
                  key={`chart-${normalizedSymbol}`}
                  symbol={normalizedSymbol}
                />
              </div>

              <div className="bg-secondary border border-border rounded-xl p-4">
                <h3 className="text-sm font-bold text-text-primary flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-accent" />
                  AI Analysis
                </h3>
                <TokenAnalysisSection tokenSymbol={baseSymbol} />
              </div>
            </div>
          )}
        </div>
      </div>

      <PositionTradeDrawer
        userId={userId}
        symbol={normalizedSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={setIsTradeDrawerOpen}
        onGoToPosition={() => {}}
      />

      <ReferralBottomNav />

      {/* Back FAB - only when viewing token analysis */}
      {isAnalysisView && (
        <button
          onClick={handleBackToDashboard}
          className="fixed bottom-24 right-4 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-accent hover:bg-accent/90 text-white shadow-lg transition-all"
          aria-label="Back"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};
